import { useMemo } from 'react';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useExchangeRates from '@/shared/data/useExchangeRates';
import getBuyerStats from '@/utils/getBuyerStats';
import useIsNewDivision from '@/utils/useIsNewDivision';
import { getNewDivisionAlertEligibleTakerMarkets } from '../components/NewDivisionAlert';
import { TableSortDirection } from '../components/Table';
import useGroupByPricingType, { TakerMarketGroupData } from './useGroupByPricingType';

export interface BuyerGroup {
  makerOrganizationUuid: string;
  makerId: number;
  makerOrganizationLogo: string | null;
  makerOrganizationName: string;
  takerMarkets: TakerMarket[];
}

export interface BuyerGroupWithStats extends BuyerGroup {
  stats: ReturnType<typeof getBuyerStats>;
  takerMarketGroups: TakerMarketGroupData;
}

type SortKey = 'totalInvoiceAmount' | 'eligibleInvoiceAmount';

type Sort = { key: SortKey; direction: TableSortDirection } | null;

export const sortTakerMarkets = (
  takerMarkets: TakerMarket[] = [],
  sort: Sort,
  getNewDivisionAlertEligible?: (takerMarkets: TakerMarket[]) => TakerMarket[]
) => {
  // defaults to eligibleInvoiceAmountUsd/desc
  if (!sort?.key || !sort?.direction) {
    return takerMarkets;
  }

  return takerMarkets.sort((a, b) => {
    const divisionNameA = a.marketType ? `${a.makerDivisionName}-${a.legacyMarketId}` : a.makerDivisionName;
    const divisionNameB = b.marketType ? `${b.makerDivisionName}-${b.legacyMarketId}` : b.makerDivisionName;

    // First, check if either market is a new division
    if (getNewDivisionAlertEligible) {
      const aIsNew = getNewDivisionAlertEligible([a]).includes(a);
      const bIsNew = getNewDivisionAlertEligible([b]).includes(b);

      // If one is new and the other isn't, the new one always comes first
      if (aIsNew && !bIsNew) {
        return -1;
      }

      if (!aIsNew && bIsNew) {
        return 1;
      }
      // If both are new or both are not new, fall through to the regular sorting below
    }

    if (sort.key === 'totalInvoiceAmount') {
      return sort.direction === 'desc'
        ? b.totalInvoiceAmountUsd - a.totalInvoiceAmountUsd || divisionNameA.localeCompare(divisionNameB)
        : a.totalInvoiceAmountUsd - b.totalInvoiceAmountUsd || divisionNameA.localeCompare(divisionNameB);
    }

    if (sort.key === 'eligibleInvoiceAmount') {
      return sort.direction === 'desc'
        ? b.eligibleInvoiceAmountUsd - a.eligibleInvoiceAmountUsd || divisionNameA.localeCompare(divisionNameB)
        : a.eligibleInvoiceAmountUsd - b.eligibleInvoiceAmountUsd || divisionNameA.localeCompare(divisionNameB);
    }

    return sort.direction === 'desc'
      ? b.totalInvoiceAmountUsd - a.totalInvoiceAmountUsd || divisionNameA.localeCompare(divisionNameB)
      : a.totalInvoiceAmountUsd - b.totalInvoiceAmountUsd || divisionNameA.localeCompare(divisionNameB);
  });
};

const useGroupByBuyer = ({
  takerMarkets,
  sortByEligible = false,
  prioritizeNewDivisions = false,
}: {
  takerMarkets: TakerMarket[];
  sortByEligible?: boolean;
  prioritizeNewDivisions?: boolean;
}): BuyerGroupWithStats[] => {
  const { isNewDivision } = useIsNewDivision();
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();
  const { groupByPricingType } = useGroupByPricingType();
  const getNewDivisionAlertEligible = prioritizeNewDivisions ? getNewDivisionAlertEligibleTakerMarkets : undefined;

  return useMemo(() => {
    const buyers = takerMarkets.reduce(
      (acc, takerMarket) => {
        const {
          makerOrganizationUuid,
          makerDivisionId: makerId,
          makerOrganizationLogo,
          makerOrganizationName,
        } = takerMarket;
        const existingBuyer = acc[makerOrganizationUuid];
        const totalKey = sortByEligible ? 'eligibleInvoiceAmountUsd' : 'totalInvoiceAmountUsd';

        acc[makerOrganizationUuid] = {
          makerOrganizationUuid,
          makerId,
          makerOrganizationLogo,
          makerOrganizationName,
          takerMarkets: existingBuyer?.takerMarkets ? [...existingBuyer.takerMarkets, takerMarket] : [takerMarket],
          total: (existingBuyer?.total ?? 0) + takerMarket[totalKey], // store total for sorting
        };

        return acc;
      },
      {} as Record<string, BuyerGroup & { total: number }>
    );

    const buyersArray = Object.values(buyers)
      .sort((a, b) => b.total - a.total || a.makerOrganizationName.localeCompare(b.makerOrganizationName))
      .map(({ total: _, ...buyer }) => ({
        ...buyer,
        takerMarkets: sortTakerMarkets(
          buyer.takerMarkets,
          {
            key: sortByEligible ? 'eligibleInvoiceAmount' : 'totalInvoiceAmount',
            direction: 'desc',
          },
          getNewDivisionAlertEligible
        ),
        takerMarketGroups: groupByPricingType(buyer.takerMarkets),
        stats: getBuyerStats({
          buyer,
          exchangeRates,
          isNewDivision,
          selectedCurrency,
          includeAwardedInvoices: true,
          getNewDivisionAlertEligible,
        }),
      }));

    if (prioritizeNewDivisions) {
      // If enableNewDivisionAlert, sort buyers by new division count
      // Also sort takerMarkets in the buyer to put new divisions first
      return buyersArray.sort((a, b) => b.stats.newDivisionCount - a.stats.newDivisionCount);
    }

    return buyersArray;
  }, [
    exchangeRates,
    groupByPricingType,
    isNewDivision,
    selectedCurrency,
    sortByEligible,
    takerMarkets,
    getNewDivisionAlertEligible,
    prioritizeNewDivisions,
  ]);
};

export default useGroupByBuyer;
